import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// Simple Interest Calculator — 4 tabs (Balance / Principal / Term / Rate)
export default function SimpleInterestCalc() {
  const [tab, setTab] = useState('balance');
  const [P, setP] = useState(20000);
  const [R, setR] = useState(3);
  const [T, setT] = useState(10);
  const [balance, setBalance] = useState(30000);

  const c = useMemo(() => {
    // Solve unknown → resolve full set of P, R, T, balance
    let pR = P, rR = R, tR = T, bR = balance, solved = { label: 'End Balance', value: 0 };
    if (tab === 'balance') {
      const interest = P * R/100 * T;
      bR = P + interest; solved = { label: 'End Balance', value: bR };
    } else if (tab === 'principal') {
      pR = balance / (1 + R/100 * T); solved = { label: 'Required Principal', value: pR };
    } else if (tab === 'term') {
      tR = (balance/P - 1) / (R/100); solved = { label: 'Required Term', value: tR, unit: ' years' };
    } else if (tab === 'rate') {
      rR = ((balance/P - 1) / T) * 100; solved = { label: 'Required Interest Rate', value: rR, unit: '%' };
    }
    const interest = pR * rR/100 * tR;
    const finalBalance = pR + interest;
    return { pR, rR, tR, bR: finalBalance, interest, solved };
  }, [tab, P, R, T, balance]);

  const tabs = [
    { key:'balance',   label:'Balance' },
    { key:'principal', label:'Principal' },
    { key:'rate',      label:'Rate' },
    { key:'term',      label:'Term' },
  ];

  const yInt = Math.max(1, Math.ceil(c.tR));
  const scheduleRows = Array.from({ length: yInt }, (_, i) => {
    const y = i + 1;
    const frac = y > c.tR ? c.tR - (y - 1) : 1;
    const yearInt = c.pR * c.rR / 100 * frac;
    const totalInt = i === yInt - 1 ? c.interest : c.pR * c.rR / 100 * y;
    return { year: y, principal: c.pR, yearInt, totalInt, balance: c.pR + totalInt };
  });

  const solvedDisplay = c.solved.unit
    ? `${c.solved.value.toFixed(c.solved.unit === '%' ? 4 : 3)}${c.solved.unit}`
    : fmt(c.solved.value);

  return (
    <CalcShell look="cnet" title="Simple Interest Calculator" subtitle="Solve for end balance, principal, interest rate, or term using simple interest (I = P·R·T)."
      about={<><p><b>Simple interest</b> is calculated on the original principal only, not on accumulated interest. Formula: <b>I = P × R × T</b>.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200" data-print="inputs">
        {tabs.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab===t.key?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`} data-testid={`si-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-print="inputs">
          {tab !== 'principal' && <div className="field"><label>Principal</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={P} onChange={e=>setP(+e.target.value||0)} className="flex-1"/></div></div>}
          {tab !== 'rate' && <div className="field"><label>Interest rate</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" step="0.01" value={R} onChange={e=>setR(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
              <select defaultValue="year"><option value="year">per year</option><option value="month">per month</option></select>
            </div>
          </div>}
          {tab !== 'term' && <div className="field"><label>Term</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={T} onChange={e=>setT(+e.target.value||0)}/>
              <select defaultValue="years"><option value="years">years</option><option value="months">months</option></select>
            </div>
          </div>}
          {tab !== 'balance' && <div className="field"><label>End Balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>}
        </div>
        <div data-print="result">
          <div className="result-panel mb-3">
            <div className="result-title">{c.solved.label}</div>
            <div className="result-value" data-testid="si-primary">{solvedDisplay}</div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Principal</td><td className="text-right">{fmt(c.pR)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
            <tr><td className="font-semibold">End Balance</td><td className="text-right font-bold">{fmt(c.bR)}</td></tr>
          </tbody></table>
          <div className="mt-4">
            <PieChart
              parts={[
                { label: 'Principal', value: c.pR,      color: '#2563eb' },
                { label: 'Interest',  value: c.interest, color: '#10b981' },
              ]}
              size={170}
            />
          </div>
        </div>
      </div>

      <div data-print="tables">
        <div className="calc-card p-5 mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
          <BarChart
            data={scheduleRows.map(r => ({
              label: String(r.year),
              values: [
                { value: r.principal, color: '#2563eb', label: 'Principal' },
                { value: r.totalInt,   color: '#f97316', label: 'Interest' },
              ],
            }))}
            stack={true}
            legend={[
              { label: 'Principal', color: '#2563eb' },
              { label: 'Interest',  color: '#f97316' },
            ]}
          />
        </div>

        <div className="calc-card p-5 mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Accumulation Schedule</h3>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm" data-testid="si-schedule-table">
              <thead className="bg-blue-800 text-white sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Year</th>
                  <th className="px-3 py-2 text-right">Principal</th>
                  <th className="px-3 py-2 text-right">Interest (this year)</th>
                  <th className="px-3 py-2 text-right">Ending balance</th>
                </tr>
              </thead>
              <tbody>
                {scheduleRows.map((r,i)=>(
                  <tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}>
                    <td className="px-3 py-2">{r.year}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.principal)}</td>
                    <td className="px-3 py-2 text-right text-orange-600">{fmt(r.yearInt)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <StepsPanel steps={[
        `Simple interest formula: <b>I = P × R × T</b>`,
        `Solved for <b>${c.solved.label}</b> = ${solvedDisplay}. Resolved knowns: P = ${fmt(c.pR)}, R = ${c.rR.toFixed(4)}%, T = ${c.tR.toFixed(3)} years.`,
        `Total interest = ${fmt(c.pR)} × ${c.rR.toFixed(3)}% × ${c.tR.toFixed(2)} = <b>${fmt(c.interest)}</b>. End balance = <b>${fmt(c.bR)}</b>.`,
      ]}/>
    </CalcShell>
  );
}
